import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, clearMsalCacheForce, clearMsalInteractionCache } from '../authConfig';
import { supabase } from '../supabaseClient';
import { useSyncState } from '../hooks/useSyncState';
import { CompanyConfig } from '../types';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

const LoginScreen: React.FC = () => {
    const { instance, inProgress } = useMsal();
    const [showExternal, setShowExternal] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isMobileRedirect, setIsMobileRedirect] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [otpInput, setOtpInput] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);

    const [msalStatusDescription, setMsalStatusDescription] = useState('');
    const [loginInProgress, setLoginInProgress] = useState(false);
    const loginInProgressRef = React.useRef(false);
    
    const setLoginInProgressStateAndRef = (val: boolean) => {
        setLoginInProgress(val);
        loginInProgressRef.current = val;
    };
    
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const resetMicrosoftLoginState = (reason?: string) => {
        console.log("[MSAL Mobile] resetMicrosoftLoginState ejecutado. Razón:", reason);
        setLoading(false);
        setMsalStatusDescription(reason || "");
        setLoginInProgressStateAndRef(false);
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        
        // Limpiar caché de interacción temporal
        clearMsalInteractionCache();
        
        // Anti-loop: Guardar flag si es cancelación
        if (reason && (reason.includes("cancelado") || reason.includes("cancel") || reason.includes("Cierre") || reason.includes("cerrado"))) {
            localStorage.setItem('microsoftLoginCancelled', 'true');
        }
        
        // Cerrar navegador
        if (Capacitor.isNativePlatform()) {
            Browser.close().catch(e => {
                console.log("[MSAL Mobile] Error al cerrar navegador:", e);
            });
        }
    };

    useEffect(() => {
        if (inProgress === 'handleRedirect') {
            setLoading(true);
            setMsalStatusDescription("Procesando inicio de sesión con Microsoft...");
        } else if (inProgress === 'acquireToken') {
            setLoading(true);
            setMsalStatusDescription("Redirigiendo a Microsoft...");
        } else if (inProgress === 'none' && loading && (msalStatusDescription.includes("Microsoft") || msalStatusDescription.includes("Redirigiendo"))) {
            setLoading(false);
            setMsalStatusDescription("");
        }
    }, [inProgress, loading, msalStatusDescription]);

    const [companyConfigs] = useSyncState<CompanyConfig[]>('data', 'global_company_config', [
        {
            name: 'Empresa Holding', rut: '', razonSocial: '', direccion: '', logo: undefined
        }
    ]);
    const companyConfig = companyConfigs[0];

    useEffect(() => {
        const path = window.location.pathname;
        const isAuthRoute = 
            path === '/auth-callback' || 
            path === '/auth/invite' || 
            path === '/auth/otp' || 
            path === '/auth/callback' || 
            path === '/auth/confirm';

        if (!isAuthRoute) {
            // Enforce clean auth state on login mount to avoid corrupt session header issues
            supabase.auth.signOut().catch(console.error);
            // También limpiar caché de interacción de MSAL por seguridad al montar el Login
            clearMsalInteractionCache();
        }

        const isMobileDevice = /android|iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /Macintosh/.test(navigator.userAgent));
        const isNativePlatform = Capacitor.isNativePlatform();
        if (isNativePlatform || isMobileDevice) {
            localStorage.setItem('isNative', 'true');
        }

        if (isAuthRoute) {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');
            
            // Deduce fallback type based on path prefix
            let type = params.get('type');
            if (!type) {
                if (path.includes('/invite')) type = 'invite';
                else if (path.includes('/otp')) type = 'magiclink';
                else if (path.includes('/callback')) type = 'magiclink';
                else if (path.includes('/confirm')) type = 'signup';
                else type = 'magiclink';
            }
            
            if (token) {
                const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
                const isNative = Capacitor.isNativePlatform();
                
                if (isMobile && !isNative) {
                    setMessage('Detectamos que estás en un dispositivo móvil. Para entrar a K-LyDapp, presiona el botón abajo para abrir la aplicación nativa.');
                    setRedirectUrl(`klydapp://auth?token=${token}&type=${type}`);
                    setIsMobileRedirect(true);
                    return;
                }

                setLoading(true);
                setMessage('Procesando enlace de acceso seguro...');
                (supabase.auth as any).verifyOtp({ token_hash: token, type: type })
                    .then(({ error }: any) => {
                        if (error) {
                            setMessage('El enlace ha expirado o ya fue utilizado.');
                            setLoading(false);
                        } else {
                            setMessage('Acceso autorizado. Cargando plataforma...');
                            window.history.replaceState({}, document.title, "/");
                        }
                    })
                    .catch(() => {
                        setMessage('Error al verificar el enlace.');
                        setLoading(false);
                    });
            }
        }
        
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleBrowserClosed = () => {
            console.log("[LoginScreen] Evento 'msal-browser-closed' recibido. Reseteando estado MSAL.");
            resetMicrosoftLoginState("Inicio de sesión cancelado. Puedes intentarlo nuevamente.");
        };

        window.addEventListener('msal-browser-closed', handleBrowserClosed);
        return () => {
            window.removeEventListener('msal-browser-closed', handleBrowserClosed);
        };
    }, []);

    const handleLogin = () => {
        // Limpiar el flag de cancelación al pulsar el botón
        localStorage.removeItem('microsoftLoginCancelled');
        
        const isNative = Capacitor.isNativePlatform();
        if (loading || (!isNative && inProgress !== 'none')) {
            console.log("[LoginScreen] Login en curso o inProgress activo, ignorando click.");
            return;
        }
        
        setLoading(true);
        setMsalStatusDescription("Redirigiendo a Microsoft...");
        setLoginInProgressStateAndRef(true);

        // Iniciar timeout defensivo de 180 segundos (3 minutos)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            if (loginInProgressRef.current) {
                console.warn("[MSAL Mobile] Timeout defensivo de 180s alcanzado sin respuesta. Liberando UI.");
                resetMicrosoftLoginState("El inicio de sesión tomó demasiado tiempo o fue cancelado. Inténtalo nuevamente.");
            }
        }, 180000);

        instance.loginRedirect(loginRequest).catch(e => {
            console.error("[MSAL Mobile] Error al iniciar redirección de Microsoft:", e);
            resetMicrosoftLoginState("Error al iniciar redirección de Microsoft: " + (e.message || e));
        });
    };

    const handleExternalLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            const { data, error } = await supabase.functions.invoke('magic-link-login', {
                body: { email }
            });

            if (error || (data && data.error)) {
                const actualError = error?.message || data?.error;
                console.error("Function Error:", actualError);
                
                if (actualError && (actualError.includes('not found') || actualError.includes('Signups not allowed') || actualError.includes('not_found'))) {
                    setMessage('Acceso denegado: Este correo no está invitado o registrado.');
                } else if (actualError && (actualError.includes('rate limit') || actualError.includes('60 seconds') || actualError.includes('too many requests') || actualError.includes('rate_limit'))) {
                    setMessage('Debes esperar unos minutos antes de solicitar otro código.');
                } else {
                    setMessage('Error al procesar: ' + actualError);
                }
            } else if (data && data.email_status === 'failed') {
                setMessage('Se generó el acceso, pero falló el envío del correo.');
                alert(`⚠️ Se generó el acceso, PERO el correo falló al enviarse.\n\nMotivo del servidor: ${data.email_error}\n\nEnlace manual de acceso:\n${data.action_link}\n\nCódigo OTP manual: ${data.email_otp}`);
                setShowOtpInput(true);
            } else {
                setMessage('Se ha enviado un código de acceso a su correo electrónico.');
                setShowOtpInput(true);
            }
        } catch (err: any) {
            console.error("Network Error:", err);
            setMessage("Error de red al conectar con el servidor.");
        }
        setLoading(false);
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            let { data, error } = await supabase.auth.verifyOtp({
                email,
                token: otpInput,
                type: 'email'
            });

            if (error) {
                console.log("Email OTP verification failed, trying magiclink type...", error.message);
                const retry = await supabase.auth.verifyOtp({
                    email,
                    token: otpInput,
                    type: 'magiclink'
                });
                data = retry.data;
                error = retry.error;
            }

            if (error) {
                console.error("Verification error:", error);
                setMessage('Código incorrecto o expirado. Por favor, intente de nuevo.');
            } else {
                setMessage('Acceso concedido. Cargando plataforma...');
                window.history.replaceState({}, document.title, "/");
            }
        } catch (err: any) {
            console.error("Network or verification error:", err);
            setMessage("Error al verificar el código.");
        }
        setLoading(false);
    };

    return (
        <div 
            className="flex min-h-screen w-full items-center justify-center bg-slate-50 dark:bg-[#1a2634] text-[#0d131b] dark:text-white font-sans p-4 overflow-y-auto"
            style={{
                paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))",
                paddingBottom: "calc(5rem + env(safe-area-inset-bottom, 0px))"
            }}
        >
            <div className="flex flex-col items-center bg-white dark:bg-[#202e3f] p-8 md:p-10 rounded-2xl shadow-xl max-w-sm w-full border border-slate-200 dark:border-slate-800 text-center my-auto shrink-0">
                <div className="flex items-center justify-center mb-5 h-16 shrink-0">
                    <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt={companyConfig?.name || 'Constructora LD'} className="hidden md:block max-h-full max-w-full object-contain" />
                    <img src="/assets/play_store_icon.png" alt="K-LyDapp Icon" className="md:hidden max-h-full max-w-full object-contain rounded-2xl" />
                </div>
                <h1 className="text-2xl font-bold mb-2">K-LyDapp</h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">
                    Aplicación para el control de Obras
                </p>

                <div className="w-full space-y-4">
                    <button
                        onClick={handleLogin}
                        disabled={loading || (!Capacitor.isNativePlatform() && inProgress !== 'none') || loginInProgress}
                        className="flex flex-row items-center justify-center gap-3 w-full bg-[#2F2F2F] hover:bg-[#1f1f1f] dark:bg-slate-700 dark:hover:bg-slate-600 text-white py-3.5 px-4 rounded-xl font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 0H0V10H10V0Z" fill="#F25022"/>
                            <path d="M21 0H11V10H21V0Z" fill="#7FBA00"/>
                            <path d="M10 11H0V21H10V11Z" fill="#00A4EF"/>
                            <path d="M21 11H11V21H21V11Z" fill="#FFB900"/>
                        </svg>
                        <span>Usuarios LD Constructora</span>
                    </button>
                    
                    {msalStatusDescription && (
                        <p className={`text-xs text-center mt-2 font-medium ${msalStatusDescription.includes('Error') || msalStatusDescription.includes('error') || msalStatusDescription.includes('cancelado') || msalStatusDescription.includes('falló') || msalStatusDescription.includes('tiempo') ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                            {msalStatusDescription}
                        </p>
                    )}

                    {loginInProgress && (
                        <button
                            type="button"
                            onClick={() => resetMicrosoftLoginState("Inicio de sesión cancelado.")}
                            className="text-xs font-semibold text-red-500 hover:text-red-700 underline mt-2 block mx-auto"
                        >
                            ¿Problemas para iniciar sesión? Cancelar y reintentar
                        </button>
                    )}
                    
                    <div className="py-2"></div>

                    {isMobileRedirect ? (
                        <div className="flex flex-col gap-4 mt-2">
                            <a 
                                href={redirectUrl}
                                className="flex flex-row items-center justify-center w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 px-4 rounded-xl font-bold transition-colors shadow-md shadow-emerald-600/20 text-lg"
                            >
                                <span className="material-symbols-outlined mr-2">open_in_new</span>
                                Abrir en la Aplicación
                            </a>
                            <p className={`text-sm text-center mt-2 font-medium ${message.includes('error') ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                                {message}
                            </p>
                        </div>
                    ) : !showExternal ? (
                        <>
                            <button
                                onClick={() => setShowExternal(true)}
                                disabled={loading || (!Capacitor.isNativePlatform() && inProgress !== 'none') || loginInProgress}
                                className="flex flex-row items-center justify-center w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 py-3.5 px-4 rounded-xl font-medium transition-colors border border-slate-200 dark:border-slate-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[20px] mr-2">mail</span>
                                Usuarios Externos a LD
                            </button>
                            {message && (
                                <p className={`text-xs text-center mt-3 font-medium ${message.includes('Error') || message.includes('error') || message.includes('falló') ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                                    {message}
                                </p>
                            )}
                        </>
                    ) : showOtpInput ? (
                        <form onSubmit={handleVerifyOtp} className="flex flex-col gap-3 text-left">
                            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Código de Verificación (OTP)</label>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                Ingresa el código de 6 dígitos enviado a <strong>{email}</strong>:
                            </p>
                            <input
                                type="text"
                                maxLength={6}
                                pattern="\d{6}"
                                required
                                value={otpInput}
                                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                                placeholder="000000"
                                className="w-full text-center text-2xl font-bold tracking-[0.5em] border border-slate-300 dark:border-slate-600 rounded-xl p-3 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-orange-500 text-slate-800 dark:text-white"
                            />
                            <button
                                type="submit"
                                disabled={loading || otpInput.length !== 6}
                                className="flex flex-row items-center justify-center w-full bg-orange-600 hover:bg-orange-700 text-white py-3.5 px-4 rounded-xl font-medium transition-colors disabled:opacity-50 mt-1 shadow-md shadow-orange-600/20"
                            >
                                {loading ? 'Verificando...' : 'Verificar e Ingresar'}
                            </button>
                            {message && (
                                <p className={`text-xs text-center mt-2 font-medium ${message.includes('incorrecto') || message.includes('Error') || message.includes('error') || message.includes('falló') ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                                    {message}
                                </p>
                            )}
                            <div className="flex justify-between items-center mt-3">
                                <button type="button" onClick={() => {setShowOtpInput(false); setOtpInput(''); setMessage('');}} className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                                    Atrás
                                </button>
                                <button type="button" onClick={handleExternalLogin} disabled={loading} className="text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors disabled:opacity-50">
                                    Reenviar código
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleExternalLogin} className="flex flex-col gap-3 text-left">
                            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Correo Registrado en Obra</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="ejemplo@gmail.com"
                                className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-xl p-3 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-orange-500 text-slate-800 dark:text-white"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex flex-row items-center justify-center w-full bg-orange-600 hover:bg-orange-700 text-white py-3.5 px-4 rounded-xl font-medium transition-colors disabled:opacity-50 mt-1 shadow-md shadow-orange-600/20"
                            >
                                {loading ? 'Enviando...' : 'Obtener Código de Acceso'}
                            </button>
                            {message && (
                                <p className={`text-xs text-center mt-2 font-medium ${message.includes('error') || message.includes('denegado') || message.includes('falló') ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                                    {message}
                                </p>
                            )}
                            <button type="button" onClick={() => {setShowExternal(false); setMessage('')}} className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-center w-full mt-2 transition-colors">
                                Cancelar
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
